import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { DataFormat } from "utils";
import { DemurrageCalculationRoundingRule } from "__generated__/graphql";
import { Fragment, ReactNode } from "react";
import { CalculationMethodDetailsByTypeProps } from "@schemas/calculationmethods/schema";
import { FormattedMinutes } from "utils/DateUtils";
import { CallType } from "__generated__/enums.ts";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";

type DetailsField = {
	id: string;
	render: () => ReactNode;
}

const DemurrageCalculationCalculationMethodDetails = ({ calculationMethod, currency, session, hiddenFields }: CalculationMethodDetailsByTypeProps) => {

	const LaytimeAllowedAndPeriodRegimeFields: DetailsField[] = [
		{
			id: "calculationMethod.laytimePeriodRegime",
			render: () => (
				<Grid size={6}>
					<FormGroup label={intl.formatMessage({ id: "PERIOD_REGIME" })} overflow="hidden">
						{calculationMethod.laytimePeriodRegime ? intl.formatMessage({ id: `PERIOD_REGIME_${calculationMethod.laytimePeriodRegime}` }) : "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeAllowed",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "LAYTIME_ALLOWED" })} overflow="hidden">
						{calculationMethod.laytimeAllowed
							? <FormattedMinutes minutes={calculationMethod.laytimeAllowed} />
							: "-"}
					</FormGroup>
				</Grid>
			),
		},
	];

	const LaytimeRoundingFields: DetailsField[] = [
		{
			id: "calculationMethod.laytimePeriodRoundingRule",
			render: () => calculationMethod.laytimeRoundingRule !== DemurrageCalculationRoundingRule.None && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "ROUNDING_RULE" })} overflow="hidden">
						{intl.formatMessage({ id: `ROUNDING_RULE_${calculationMethod.laytimeRoundingRule}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeRoundingUnit",
			render: () => calculationMethod.laytimeRoundingUnit && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "ROUNDING_UNIT" })} overflow="hidden">
						{intl.formatMessage({ id: `ROUNDING_UNIT_${calculationMethod.laytimeRoundingUnit}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeRoundingScope",
			render: () => calculationMethod.laytimeRoundingScope && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "ROUNDING_SCOPE" })} overflow="hidden">
						{intl.formatMessage({ id: `ROUNDING_SCOPE_${calculationMethod.laytimeRoundingScope}` })}
					</FormGroup>
				</Grid>
			),
		},
	];

	const DemurrageCalculationCalculationMethodDetailsFields: DetailsField[] = [
		{
			id: "calculationMethod.price",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${calculationMethod.demurrageCalculationUnit}` })} overflow="hidden">
						{DataFormat.price(calculationMethod.price, currency.isoCode, session.locale)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeAllowedAndPeriodRegime",
			render: () => (
				<Grid container size={12} spacing={2}>
					{LaytimeAllowedAndPeriodRegimeFields.map(x => x.render())}
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeRounding",
			render: () => (
				<Grid container size={12} spacing={2}>
					{LaytimeRoundingFields.map(x => x.render())}
				</Grid>
			),
		},
		{
			id: "calculationMethod.defaultCargoActionTypes",
			render: () => {
				const callTypeOrder = [ CallType.NitrogenPurging, CallType.Loading, CallType.Discharging, CallType.Cleaning ];
				return <Grid size={10}>
					<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_DEFAULT_CARGO_ACTION_TYPES" })} overflow="hidden">
						<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
							{calculationMethod.defaultCargoActionTypes
								?.sort((a, b) => callTypeOrder.indexOf(a) - callTypeOrder.indexOf(b))
								.map((x) =>
									<CargoActionTypeTag key={x} type={x} />)
							}
						</span>
					</FormGroup>
				</Grid>;
			},
		},
		{
			id: "calculationMethod.description",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_CALCULATION_INSTRUCTIONS" })} overflow="hidden">
						{calculationMethod.description || "-"}
					</FormGroup>
				</Grid>
			),
		},
	];

	return (
		<>
			{DemurrageCalculationCalculationMethodDetailsFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default DemurrageCalculationCalculationMethodDetails;
